import {TypePortee} from '../workflow/workflow';
import {ModeParticipants} from "./ns-params";
import {ParticipantExterneParam} from "../participant/participant-externe-param";
import {Devise} from "./devise";
import {EnergieFiscale} from "../vehicule/energiefiscale";
import {TypeVehicule} from "../vehicule/typevehicule";
import {TypeTraiterAvantLe} from "../od/type-traiter-avant-le";
import {Field} from "./field";
import {Role} from "../workflow/role";
import {Statut} from "../workflow/statut";
import {ConfigCompte} from "./configCompte";
import {TypeEntite} from '@domain/typeentite/typeEntite';
import {TypeDateLotComptable} from "@domain/admin/comptabilite/optionsTransverses";
import {TypeDocument} from "@domain/admin/parametre/demat/type-document";
import {PieceJointeParam} from "@domain/admin/parametre/demat/piece-jointe-param";

/**
 * Interface des paramètres globaux
 */
export interface SettingsGlobalState {
    /* Données */
    idPays?: number;
    isOrgaFixe?: boolean;
    nbRepartitionsMax?: number;
    listeFieldParams?: Array<Field>;
    analytiqueUserVisibility?: boolean;
    analytiqueUserManagement?: boolean;
    analytiqueAdminVisibility?: boolean;
    analytiqueAdminManagement?: boolean;
    listePeriodes?: Array<any>;
    idPeriodeEnCours?: number;
    listeDevises?: Array<Devise>;
    listeExtensions?: Array<string>;
    isReferentielExterneAvailable?: boolean;
    budgetNiveauNonBloquant?: number;
    budgetNiveauBloquant?: number;
    ndfActif: boolean;
    odActif: boolean;
    avActif: boolean;
    otActif: boolean;
    fcActif: boolean;
    opeCarteActif: boolean;

    /** Config du profil */
    profilAlerteConfig?: any;

    /** Devise de l'entreprise */
    deviseEntreprise?: string;

    /** Libellé de l'axe 5 (analytique) */
    libelleAxe5?: string;

    /** Libellé de l'axe 6 (analytique) */
    libelleAxe6?: string;

    /** Libellé du champ OPERATION_1 (analytique) */
    libelleField5?: string;

    /** Libellé du champ OPERATION_2 (analytique) */
    libelleField6?: string;

    /** Taux inversé */
    isTauxInverse?: boolean;

    /** Gestion carte paiement */
    canSeeCartePaiement?: boolean;

    /** Gestion de la configuration du compte */
    configCompte?: ConfigCompte;

    /** Suivi budgétaire activé ou non */
    isSuiviBudgetaire?: boolean;

    /** Taille limite des PJ selon l'objet en cours */
    tailleMax?: number;

    /** Logo personnalisé */
    logoPersonnalise: string;

    /** logo secondaire */
    logoSecondaire: string;

    /** dématérialisation à valeur probante activée */
    dematProbante: boolean;

    /** Type de date pour les lots comptables */
    typeDateLot?: TypeDateLotComptable;

    /** Autoriser la saisie d'adresse personnelle par les collaborateurs */
    autoriserAdressePerso?: boolean;

    /** Autoriser la saisie d'adresse professionnelle par les collaborateurs */
    autoriserAdressePro?: boolean;
}

export interface SettingsWFObjectState extends SettingsGlobalState {
    /** Contenu du filtre rôle sur les listes WF */
    filtresRole: Role[];

    /** Contenu du filtre statut sur les listes WF */
    filtresStatut: Statut[];

    /** Contient la liste des types entités liés à la portée de l'objet WF */
    typeEntiteList: TypeEntite[];

    /** Contient la liste des types entités (pour les filtres de listes) liés à la portée de l'objet WF */
    typeEntitesFiltresList: TypeEntite[];

    /** Indique si on peut intégrer des PJ sur la portée */
    isPorteePjActif?: boolean;

    /** Contient la liste des ids des types entités liés à la portée de l'objet WF et paramétré pour les PJ */
    listeTypeEntiteIdPjAutorise: number[];

    /** Types de documents possibles pour les pièces jointes */
    typesDocument: TypeDocument[];

    /** Paramètres de pièces jointes */
    pieceJointeParams: PieceJointeParam;
}

/**
 * Interface des paramètres pour la note de frais
 */
export interface SettingsNFState extends SettingsWFObjectState {
    /* Données */
    /** Affichage du mois d'imputation */
    isPeriode?: boolean;
    /** Affichage du mois d'imputation avec une valeur par défaut */
    isPeriodeObligatoire?: boolean;
    isReferentielParticipantsExternes?: boolean;
    canAddParticipantExterne?: boolean;
    listeParamExternes?: Array<ParticipantExterneParam>;
    isCheckDateOm?: boolean;
    isFraisRefuses?: boolean;
    isFraisRefusesCloisonne?: boolean;
    //Params relatifs aux véhicules
    isAutorisationVehiculeActif?: boolean;
    niveauControleAutorisationVehiculeNdf?: number;
    isBudgetControleNDF?: boolean;
    anomalieDepensesFutures: Anomalie;
    anomalieDepensesHorsMission: Anomalie;
    anomalieDepensesNegatives: Anomalie;
}

/**
 * Enumération des anomalies
 */
export enum Anomalie {
    NON_CONTROLE,
    NON_BLOQUANT,
    BLOQUANT = 2
}

/**
 * Interface des paramètres pour les OD
 */
export interface SettingsODState extends SettingsWFObjectState {
    /** true si les pages horaires doivent être affichées */
    isPlageHoraire: boolean;

    /** true si le MIB doit être affiché */
    isPeriode?: boolean;

    /** true si le champs traiter avant le doit être affiché */
    isTraiterAvantOblig: boolean;

    /** true si le champs traiter avant le doit être modifiable */
    isTraiterAvantModifiable: boolean;

    /** Mode des participants */
    modeParticipants: ModeParticipants;

    /** true si le collaborateur a le droit de ne pas participer à la mission */
    isCollabParticipePasAutorise: boolean;

    /** true s'il est possible de créer plusieurs NDF sur l'OD */
    isMultiNdfAutorise: boolean;

    /** Mode de dégrèvement, 0 - Aucun, 2 - Détaillé */
    idModeDegrevement: number;

    /** Mode de génération NDF */
    idModeGenerationNdf: number;

    /** true si la création d'OD pour les participants est autorisée */
    isCreationPourParticipant: boolean;

    /** true si l'émission d'OD pour les participants est autorisée */
    isEmissionPourParticipant: boolean;

    /** Autorisation d'ajout de participant externe par l'utilisateur */
    canAddParticipantExterne: boolean;

    /** Liste des paramètres pour la création de participant externe */
    listeParamExternes: Array<ParticipantExterneParam>;

    /** Type pour le champ à traiter avant le */
    typeTraiterAvantLe: TypeTraiterAvantLe;

    /** true si l'utilisateur peut réserver des transports hors de l'OD SANS que ce soit de la convenance (il a le choix) */
    isDateTransportIncluse: boolean;

    /** true si l'utilisateur peut avoir plusieurs NDF sur un OD */
    isMultiNdfAllowed?: boolean;

    /** true si le paramètre de contrôle du budget est actif sur les OD */
    isBudgetControleOM?: boolean;

    /** true si l'OD peut avoir un complément de destination */
    isComplementDestination?: boolean;

    /** Indiquer si le dossier voyage est actif */
    isDossierVoyageActif: boolean;

    /** Anomalie de dépenses hors mission */
    anomalieDepensesHorsMission?: Anomalie;

    /** Indiquer si l'autorisation de véhicule est actif */
    isAutorisationVehiculeActif: boolean;

    /** Niveau de contrôle de l'autorisation de véhicule sur les OD */
    niveauControleAutorisationVehiculeOd: number;
}

/**
 * Interface des paramètres pour l'avance
 */
export interface SettingsAVState extends SettingsWFObjectState {
    /** Données **/
    isAvanceAutomatique?: boolean;
    deviseEntreprise?: string;
    canCollabCreateAvance?: boolean;
    isAvanceHorsOM?: boolean;
    isChoixAvanceAImputer: boolean;
    isChangeAvance: boolean;
}

/**
 * Interface des paramètres pour les missions permanentes
 */
export interface SettingsOTState extends SettingsWFObjectState {
    /** Données **/
    isPeriodeAnInterditOMPermanent: boolean;
    isExerciceChevalInterditOMPermanent: boolean;
    isOmPermanentDesactiveLieu: boolean;
}

/**
 * Interface des paramètres pour les factures
 */
export interface SettingsFCState extends SettingsWFObjectState {
    /** Données **/
    isBudgetControleFacture?: boolean;
}

/**
 * Interface des paramètres pour les véhicules
 */
export interface SettingsVehiculeState extends SettingsGlobalState {
    approbationActif?: boolean;
    assuranceActif?: boolean;
    assuranceObligatoire?: boolean;
    autorisationActif?: boolean;
    carteGriseActif?: boolean;
    carteGriseObligatoire?: boolean;
    listeEnergieVehicule?: Array<EnergieFiscale>;
    listeTypeVehicule?: Array<TypeVehicule>;
    pieceJointeParam?: any;
    listeExtensions?: Array<string>;
}

/**
 * Interface représentant les paramètres applicatifs
 */
export interface SettingsState {
    /* Données */
    [TypePortee.NF]?: SettingsNFState,
    [TypePortee.AV]?: SettingsAVState,
    [TypePortee.OT]?: SettingsOTState,
    [TypePortee.OD]?: SettingsODState,
    [TypePortee.FC]?: SettingsFCState,
    [TypePortee.CPT]?: SettingsGlobalState,
    [TypePortee.BGT]?: SettingsGlobalState
    [TypePortee.VP]?: SettingsVehiculeState,
    ['Global']?: SettingsGlobalState
}
